/**
 * Custom size mantle order form (blocks/mantle-order-form.liquid).
 *
 * - Sends the measurements to Shopify's native `contact` form endpoint with
 *   fetch, so the customer stays on the product page, and shows its own
 *   "sent" or "try again" message.
 * - If Shopify answers with its bot check instead of accepting the message,
 *   the form is posted the ordinary way so the customer can complete the check
 *   and the message is not lost.
 * - Preselects the material from the variant chosen on the page when the
 *   dialog opens, if the names match.
 */
class MantleOrderForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;

    const dialogComponent = this.closest('dialog-component');
    dialogComponent?.addEventListener('dialog:open', () => this.#preselectMaterial());

    // After an ordinary (non-fetch) post the page reloads: reopen the dialog on the result.
    if (this.form.querySelector('[data-mantle-posted]')) {
      queueMicrotask(() => dialogComponent?.showDialog?.());
    }

    this.form.addEventListener('submit', (event) => this.#handleSubmit(event));
  }

  #preselectMaterial() {
    if (this.form.querySelector('[name="contact[Material]"]:checked')) return;
    const chosen = document.querySelector('.variant-picker input[type="radio"]:checked, .variant-picker select');
    const name = (chosen?.value || '').trim().toLowerCase();
    if (!name) return;
    for (const radio of this.form.querySelectorAll('[name="contact[Material]"]')) {
      if (radio.value.trim().toLowerCase() === name) radio.checked = true;
    }
  }

  async #handleSubmit(event) {
    event.preventDefault();
    if (!this.form.reportValidity()) return;

    const submittedAt = this.form.querySelector('[data-mantle-field="submitted-at"]');
    if (submittedAt) submittedAt.value = new Date().toISOString();

    const button = this.form.querySelector('.mantle-order__submit');
    if (button) {
      button.disabled = true;
      button.textContent = 'Sending…';
    }

    try {
      const response = await fetch(this.form.action, {
        method: 'POST',
        body: new FormData(this.form),
        credentials: 'same-origin',
      });
      if (!response.ok || response.url.includes('/challenge')) {
        this.form.submit();
        return;
      }
      this.#showSuccess();
    } catch {
      this.#showError();
      if (button) {
        button.disabled = false;
        button.textContent = button.dataset.label || 'Send for a quote';
      }
    }
  }

  #showSuccess() {
    this.form.querySelector('.mantle-order__fields')?.remove();
    this.form.querySelector('.mantle-order__error')?.remove();

    const success = document.createElement('div');
    success.className = 'mantle-order__success';
    success.tabIndex = -1;
    success.innerHTML = `
      <p class="mantle-order__success-heading">Thank you. Your measurements have been sent.</p>
      <p>Our sales team will reply with a quote.</p>
    `;
    this.form.prepend(success);
    success.focus();
  }

  #showError() {
    let error = this.form.querySelector('.mantle-order__error');
    if (!error) {
      error = document.createElement('div');
      error.className = 'mantle-order__error';
      error.tabIndex = -1;
      this.form.prepend(error);
    }
    error.textContent = 'Your measurements could not be sent. Please try again, or call us.';
    error.focus();
  }
}

if (!customElements.get('mantle-order-form')) customElements.define('mantle-order-form', MantleOrderForm);
